import getCompanyPriceApi from '@/api/get/company/company-pricing/get-company-price';
import validatePartNumberApi from '@/api/get/company/company-pricing/validate-part-number';
import getDealerSupplierPriceApi from '@/api/get/dealer/get-dealer-supplier-price';
import bulkUpdatePriceList from '@/api/patch/company/company-price/bulk-update-price-list';
import updateCompanyPriceApi from '@/api/patch/company/company-price/update-company-price';
import bulkUpdateDealerPriceList from '@/api/patch/dealer/bulk-update-dealer-price-list';
import updateDealerPriceApi from '@/api/patch/dealer/update-dealer-price';
import Alert from '@/components/alert/alert-component';
import SubmitCard from '@/components/card/submit-card-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { Col, Row } from '@/components/grid/grid-component';
import Loader from '@/components/loader/loader-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { removeUndefinedProperties } from '@/utils/utils';
import { requiredValidationMessage } from '@/utils/validations';
import PropTypes from 'prop-types';
import ReactDOMServer from 'react-dom/server';
import { useFormContext } from 'react-hook-form';
import { FiPercent } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Checkbox from '../react-hook-form-inputs/checkbox/checkbox-component';

const modalRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
};

const modalParentRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
  padding: '24px',
};

const styledCol = {
  paddingLeft: '0px',
};

const EditPricingForm = ({ selectedRows, companyId, priceId, isDealer }) => {
  const { setError, clearErrors } = useFormContext();
  const { refetch: validatePartNumber, loading: validatePartNumberLoading } =
    useLazyQuery(validatePartNumberApi);

  return (
    <Row style={modalParentRowStyle}>
      {selectedRows.length === 0 && (
        <Row>
          <Col xs={12}>
            <Input
              disabled={false}
              label="Product"
              placeholder="Please enter product name"
              type="text"
              name="price.name"
              registerOptions={{
                required: true,
              }}
            />
          </Col>
          <Col xs={12}>
            <Input
              disabled={false}
              label="Price"
              placeholder="Please enter price"
              type="text"
              name="price.cost_price"
              registerOptions={{
                required: true,
                valueAsNumber: true,
                min: {
                  value: 0,
                  message: 'Cannot be negative',
                },
              }}
            />
          </Col>
          <Col xs={12}>
            <Input
              disabled={validatePartNumberLoading}
              label="Part Number"
              placeholder="Please enter part number"
              type="text"
              name="price.part_number"
              registerOptions={{
                required: true,
                onBlur: (x) => {
                  validatePartNumber(companyId, x.target.value, priceId).then(
                    (res) => {
                      if (!res.data.valid) {
                        setError('price.part_number', {
                          message: 'This part number is already in use',
                        });
                      } else {
                        clearErrors('price.part_number');
                      }
                    },
                  );
                },
              }}
            />
          </Col>
          {!isDealer && (
            <Col xs={12}>
              <Checkbox
                disabled={false}
                label="Temporary Part Number"
                name="price.temp_pn"
              />
            </Col>
          )}
        </Row>
      )}
      <Row style={modalRowStyle}>
        <Col style={styledCol} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Input
            disabled={false}
            label="End User Gross Profit - GP%"
            placeholder="%"
            type="number"
            iconRight={<FiPercent />}
            name="price.end_user_gross_profit"
            registerOptions={{
              required: requiredValidationMessage,
              valueAsNumber: true,
              max: 99.99,
              min: {
                value: 0,
                message: 'Cannot be negative',
              },
            }}
          />
        </Col>
      </Row>
      <Row style={modalRowStyle}>
        <Col style={styledCol} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Input
            disabled={false}
            label="Transfer Price Gross Profit - GP%"
            placeholder="%"
            type="number"
            iconRight={<FiPercent />}
            name="price.transfer_price_gross_profit"
            registerOptions={{
              valueAsNumber: true,
              required: requiredValidationMessage,
              max: 99.99,
              min: {
                value: 0,
                message: 'Cannot be negative',
              },
            }}
          />
        </Col>
      </Row>
    </Row>
  );
};

EditPricingForm.propTypes = {
  selectedRows: PropTypes.array.isRequired,
  companyId: PropTypes.string.isRequired,
  priceId: PropTypes.string.isRequired,
  isDealer: PropTypes.bool,
};

EditPricingForm.defaultProps = {
  isDealer: false,
};

/**
 * @param {object} root0 - The root object containing props
 * @param {Function} root0.onClose - Callback function to close the modal
 * @param {Function} root0.refetch - Callback function to refetch the data on table
 * @param {Function} root0.setSelectedRows - state function to setSelect items
 * @param {string} root0.priceId - The ID of the price to edit
 * @param {string} root0.companyId - The ID of the supplier or Company to edit
 * @param {bool} root0.isDealer - if company is a dealer;
 * @param {Array} root0.selectedRows - The an array of ID of the prices to edit
 * @returns {JSX.Element} - The rendered JSX element
 */
const EditPricingModal = ({
  isDealer,
  companyId,
  selectedRows,
  setSelectedRows,
  priceId,
  refetch,
  onClose,
}) => {
  const navigate = useNavigate();
  const [updateDealerPrice, { loading: updateDealerPriceLoading }] =
    useMutation(updateDealerPriceApi);

  const [bulkEditDealerPricing, { loading: bulkEditDealerPriceLoading }] =
    useMutation(bulkUpdateDealerPriceList);

  const [updateCompanyPrice, { loading: updateCompanyPriceLoading }] =
    useMutation(updateCompanyPriceApi);

  const [bulkEditPricing, { loading: bulkEditPriceLoading }] =
    useMutation(bulkUpdatePriceList);

  const submitForm = (values) => {
    if (selectedRows.length > 0) {
      const submitValues = {
        price: {
          end_user_gross_profit: Number(values.price.end_user_gross_profit),
          transfer_price_gross_profit: Number(
            values.price.transfer_price_gross_profit,
          ),
        },
        priceIds: selectedRows,
      };
      if (isDealer) {
        bulkEditDealerPricing(
          companyId,
          removeUndefinedProperties(submitValues),
        ).then((re) => {
          setSelectedRows([]);
          refetch();
          onClose();
          Swal.fire({
            title: 'Import Summary',
            html: ReactDOMServer.renderToString(
              <span>{`${re.data} additional products have been updated`}</span>,
            ),
            showCloseButton: true,
            showCancelButton: false,
            confirmButtonText: 'Ok',
            showLoaderOnConfirm: true,
          });
        });
      } else {
        bulkEditPricing(
          companyId,
          removeUndefinedProperties(submitValues),
        ).then((re) => {
          setSelectedRows([]);
          refetch();
          onClose();
          Swal.fire({
            title: 'Import Summary',
            html: ReactDOMServer.renderToString(
              <span>{`${re.data} additional products have been updated`}</span>,
            ),
            showCloseButton: true,
            showCancelButton: false,
            confirmButtonText: 'Ok',
            showLoaderOnConfirm: true,
          });
        });
      }
    } else if (isDealer) {
      updateDealerPrice(
        companyId,
        priceId,
        removeUndefinedProperties({
          end_user_gross_profit: parseFloat(values.price.end_user_gross_profit),
          transfer_price_gross_profit: parseFloat(
            values.price.transfer_price_gross_profit,
          ),
          name: values.price.name,
          cost_price: parseFloat(values.price.cost_price),
          part_number: values.price.part_number,
        }),
      ).then(() => {
        refetch();
        onClose();
        Alert({
          icon: 'success',
          text: 'Price updated successfully',
        });
      });
    } else {
      updateCompanyPrice(
        companyId,
        priceId,
        removeUndefinedProperties({
          end_user_gross_profit: parseFloat(values.price.end_user_gross_profit),
          transfer_price_gross_profit: parseFloat(
            values.price.transfer_price_gross_profit,
          ),
          name: values.price.name,
          cost_price: parseFloat(values.price.cost_price),
          part_number: values.price.part_number,
          temp_pn: values?.price?.temp_pn ?? false,
        }),
      ).then(() => {
        navigate(`/app/company-management/view/${companyId}/pricing`);
        Alert({
          icon: 'success',
          text: 'Price updated successfully',
        });
      });
    }
  };

  if (
    updateCompanyPriceLoading ||
    bulkEditPriceLoading ||
    updateDealerPriceLoading ||
    bulkEditDealerPriceLoading
  ) {
    return <Loader size={50} loading />;
  }

  return (
    <FormWrapper
      defaultValues={async () => {
        if (selectedRows.length > 0) {
          if (isDealer) {
            const data = await getDealerSupplierPriceApi(
              companyId,
              selectedRows[0],
            );
            const returnData = {
              price: {
                transfer_price_gross_profit:
                  data.data.transfer_price_gross_profit,
                end_user_gross_profit: data.data.end_user_gross_profit,
              },
            };
            return returnData;
          }
          const data = await getCompanyPriceApi(companyId, selectedRows[0]);
          const returnData = {
            price: {
              transfer_price_gross_profit:
                data.data.transfer_price_gross_profit,
              end_user_gross_profit: data.data.end_user_gross_profit,
            },
          };
          return returnData;
        }
        if (isDealer) {
          const data = await getDealerSupplierPriceApi(companyId, priceId);
          const returnData = {
            price: {
              transfer_price_gross_profit:
                data.data.transfer_price_gross_profit,
              end_user_gross_profit: data.data.end_user_gross_profit,
              name: data.data.name,
              cost_price: data.data.cost_price,
              part_number: data.data.part_number,
              temp_pn: data.data.temp_pn,
            },
          };

          return returnData;
        }

        const data = await getCompanyPriceApi(companyId, priceId);
        const returnData = {
          price: {
            transfer_price_gross_profit: data.data.transfer_price_gross_profit,
            end_user_gross_profit: data.data.end_user_gross_profit,
            name: data.data.name,
            cost_price: data.data.cost_price,
            part_number: data.data.part_number,
            temp_pn: data.data.temp_pn,
          },
        };

        return returnData;
      }}
      onSubmit={submitForm}
    >
      <EditPricingForm
        selectedRows={selectedRows}
        companyId={companyId}
        priceId={priceId}
        isDealer={isDealer}
      />
      <SubmitCard
        padding="0"
        primaryButtonTitle="Save"
        primaryButtonType="submit"
        visible={updateCompanyPriceLoading || bulkEditPriceLoading}
        primaryButtonDisabled={false}
        primaryButtonWidth="112px"
        submitClick={() => {}}
        submitClickSecondary={() => {
          onClose();
        }}
        secondaryButtonType="button"
        secondaryButtonDisabled={false}
        secondaryButtonWidth="112px"
        disabled={false}
        secondaryButtonTitle="Cancel"
      />
    </FormWrapper>
  );
};

EditPricingModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  priceId: PropTypes.string.isRequired,
  selectedRows: PropTypes.array.isRequired,
  companyId: PropTypes.string.isRequired,
  refetch: PropTypes.func.isRequired,
  setSelectedRows: PropTypes.func.isRequired,
  isDealer: PropTypes.bool.isRequired,
};

export default EditPricingModal;
